import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { feeAPI, studentAPI, batchAPI } from '../../services/api';
import { formatIndianCurrency } from '../../utils/dateUtils';
import ConfirmationModal from '../ConfirmationModal';

const FeeForm = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    student_id: '',
    batch_id: '',
    total_fee: '',
    payment_type: 'one_time',
    installment_count: 1,
    discount_amount: 0,
    installment_dates: []
  });

  const [installmentDates, setInstallmentDates] = useState([]);
  const [existingFeeWarning, setExistingFeeWarning] = useState('');
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [validationError, setValidationError] = useState('');

  const { data: students, isLoading: studentsLoading } = useQuery({
    queryKey: ['students'],
    queryFn: () => studentAPI.getAll(0, 100, true).then(res => res.data),
  });

  const { data: batches, isLoading: batchesLoading } = useQuery({
    queryKey: ['batches'],
    queryFn: () => batchAPI.getAll(0, 100, true).then(res => res.data),
  });

  // Check for existing fee structure when student and batch are selected
  const { data: existingFeeStructures } = useQuery({
    queryKey: ['existingFeeStructures', formData.student_id, formData.batch_id],
    queryFn: async () => {
      if (formData.student_id && formData.batch_id) {
        try {
          const response = await feeAPI.getByStudent(formData.student_id);
          return response.data.filter(fee => 
            fee.batch_id === formData.batch_id || 
            fee.batch?.id === formData.batch_id ||
            fee.batch?.ref?.id === formData.batch_id
          );
        } catch (error) {
          return [];
        }
      }
      return [];
    },
    enabled: !!formData.student_id && !!formData.batch_id,
  });

  // Update warning message when existing fee structures are found
  useEffect(() => {
    if (existingFeeStructures && existingFeeStructures.length > 0) {
      setExistingFeeWarning(`⚠️ A fee structure already exists for this student-batch combination. Creating another one will result in duplicates.`);
    } else {
      setExistingFeeWarning('');
    }
  }, [existingFeeStructures]);

  const createMutation = useMutation({
    mutationFn: (data) => feeAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['feeStructures']);
      navigate('/fees');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Check if there are existing fee structures
    if (existingFeeStructures && existingFeeStructures.length > 0) {
      setValidationError('A fee structure already exists for this student-batch combination. Please use the existing one or contact an administrator to merge duplicates.');
      setShowValidationErrors(true);
      return;
    }
    
    // Validate total fee is at least equal to enrollment fee
    const enrollmentFee = getEnrollmentFee();
    const totalFee = parseFloat(formData.total_fee) || 0;
    if (totalFee < enrollmentFee) {
      setValidationError(`Total fee (${formatIndianCurrency(totalFee)}) cannot be less than enrollment fee (${formatIndianCurrency(enrollmentFee)})`);
      setShowValidationErrors(true);
      return;
    }
    
    const submitData = {
      ...formData,
      total_fee: parseFloat(formData.total_fee),
      discount_amount: parseFloat(formData.discount_amount),
      installment_count: parseInt(formData.installment_count),
      installment_dates: formData.payment_type === 'installments' ? installmentDates : null
    };

    createMutation.mutate(submitData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleInstallmentDateChange = (index, date) => {
    const newDates = [...installmentDates];
    newDates[index] = date;
    setInstallmentDates(newDates);
  };

  const addInstallmentDate = () => {
    setInstallmentDates([...installmentDates, '']);
  };

  const removeInstallmentDate = (index) => {
    const newDates = installmentDates.filter((_, i) => i !== index);
    setInstallmentDates(newDates);
  };

  // Update installment dates when installment count changes
  useEffect(() => {
    const count = parseInt(formData.installment_count) || 1;
    if (formData.payment_type === 'installments') {
      const currentDates = [...installmentDates];
      while (currentDates.length < count) {
        currentDates.push('');
      }
      while (currentDates.length > count) {
        currentDates.pop();
      }
      setInstallmentDates(currentDates);
    }
  }, [formData.installment_count, formData.payment_type, installmentDates]);

  const getEnrollmentFee = () => {
    const batch = getSelectedBatch();
    return batch?.fee_amount || 0;
  };

  const calculateBaseFee = () => {
    const totalFee = parseFloat(formData.total_fee) || 0;
    const enrollmentFee = getEnrollmentFee();
    return Math.max(0, totalFee - enrollmentFee);
  };

  const calculateFinalAmount = () => {
    const totalFee = parseFloat(formData.total_fee) || 0;
    const discount = parseFloat(formData.discount_amount) || 0;
    const enrollmentFee = getEnrollmentFee();
    const baseFee = totalFee - enrollmentFee;
    const discountedBaseFee = Math.max(0, baseFee - discount);
    return discountedBaseFee + enrollmentFee; // Final amount includes enrollment fee
  };

  const calculateDiscountedBaseFee = () => {
    const baseFee = calculateBaseFee();
    const discount = parseFloat(formData.discount_amount) || 0;
    return Math.max(0, baseFee - discount);
  };


  const getSelectedBatch = () => {
    return batches?.find(b => (b._id || b.id) === formData.batch_id);
  };

  if (studentsLoading || batchesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate('/fees')}
          className="flex items-center text-gray-300 hover:text-gray-100 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Fees
        </button>
        <h1 className="text-3xl font-bold text-white">New Fee Structure</h1>
        <p className="text-gray-300 mt-2">Create a new fee structure for a student</p>
      </div>

              <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Student and Batch Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="student_id" className="block text-sm font-medium text-gray-300 mb-2">
                Student *
              </label>
              <select
                id="student_id"
                name="student_id"
                value={formData.student_id}
                onChange={handleChange}
                required
                className="block w-full px-3 py-2 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 bg-gray-700 text-white"
              >
                <option value="">Select a student</option>
                {students?.map((student) => (
                  <option key={student._id || student.id} value={student._id || student.id}>
                    {student.first_name} {student.last_name} ({student.student_id})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="batch_id" className="block text-sm font-medium text-gray-300 mb-2">
                Batch *
              </label>
              <select
                id="batch_id"
                name="batch_id"
                value={formData.batch_id}
                onChange={handleChange}
                required
                className="block w-full px-3 py-2 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 bg-gray-700 text-white"
              >
                <option value="">Select a batch</option>
                              {batches?.map((batch) => (
                <option key={batch._id || batch.id} value={batch._id || batch.id}>
                  {batch.batch_name} - {batch.subject} {batch.fee_amount ? `(Enrollment: ${formatIndianCurrency(batch.fee_amount)})` : ''}
                </option>
              ))}
              </select>
            </div>
          </div>

          {/* Existing Fee Structure Warning */}
          {existingFeeWarning && (
            <div className="bg-yellow-900/20 border border-yellow-600 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-300">
                    {existingFeeWarning}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Fee Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label htmlFor="total_fee" className="block text-sm font-medium text-gray-300 mb-2">
                Total Fee (₹) *
              </label>
              <input
                type="number"
                id="total_fee"
                name="total_fee"
                value={formData.total_fee}
                onChange={handleChange}
                min="0"
                step="0.01"
                required
                className="block w-full px-3 py-2 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 bg-gray-700 text-white"
              />
            </div>

            <div>
              <label htmlFor="discount_amount" className="block text-sm font-medium text-gray-300 mb-2">
                Discount Amount (₹)
              </label>
              <input
                type="number"
                id="discount_amount"
                name="discount_amount"
                value={formData.discount_amount}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="block w-full px-3 py-2 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 bg-gray-700 text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Final Amount (₹)
              </label>
                              <div className="block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-lg font-semibold text-white">
                {formatIndianCurrency(calculateFinalAmount())}
              </div>
              <p className="text-sm text-gray-400 mt-1">
                Includes enrollment fee + base fee (after discount)
              </p>
            </div>
          </div>

          {/* Enrollment Fee Information */}
          {formData.batch_id && getEnrollmentFee() > 0 && (
            <div className="bg-blue-900/20 border border-blue-600 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-blue-300">Fee Breakdown</h3>
                  <p className="text-sm text-blue-400 mt-1">
                    This batch has an enrollment fee of {formatIndianCurrency(getEnrollmentFee())}
                  </p>
                </div>
                <div className="text-right space-y-1">
                                  <p className="text-sm text-blue-400">Total Fee: {formatIndianCurrency(parseFloat(formData.total_fee) || 0)}</p>
                <p className="text-sm text-blue-400">Enrollment Fee: {formatIndianCurrency(getEnrollmentFee())}</p>
                <p className="text-sm text-blue-400">Base Fee: {formatIndianCurrency(calculateBaseFee())}</p>
                  {parseFloat(formData.discount_amount) > 0 && (
                    <>
                                      <p className="text-sm text-blue-400">Discount: -{formatIndianCurrency(parseFloat(formData.discount_amount))}</p>
                <p className="text-sm text-blue-400 font-medium">Discounted Base Fee: {formatIndianCurrency(calculateDiscountedBaseFee())}</p>
                    </>
                  )}
                  <p className="text-sm text-blue-300 font-bold">Final Amount: {formatIndianCurrency(calculateFinalAmount())}</p>
                </div>
              </div>
            </div>
          )}

          {/* Payment Type */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Payment Type *
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="payment_type"
                  value="one_time"
                  checked={formData.payment_type === 'one_time'}
                  onChange={handleChange}
                  className="h-4 w-4 text-yellow-500 focus:ring-yellow-500 border-gray-600"
                />
                <span className="ml-2 text-sm text-gray-300">One Time Payment</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="payment_type"
                  value="installments"
                  checked={formData.payment_type === 'installments'}
                  onChange={handleChange}
                  className="h-4 w-4 text-yellow-500 focus:ring-yellow-500 border-gray-600"
                />
                <span className="ml-2 text-sm text-gray-300">Installments</span>
              </label>
            </div>
          </div>

          {/* Installment Details */}
          {formData.payment_type === 'installments' && (
            <div className="space-y-4">
              <div>
                <label htmlFor="installment_count" className="block text-sm font-medium text-gray-300 mb-2">
                  Number of Installments *
                </label>
                <input
                  type="number"
                  id="installment_count"
                  name="installment_count"
                  value={formData.installment_count}
                  onChange={handleChange}
                  min="1"
                  max="12"
                  required
                  className="block w-full px-3 py-2 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 bg-gray-700 text-white"
                />
              </div>

              <div>
                              <label className="block text-sm font-medium text-gray-300 mb-2">
                Installment Amount (₹)
              </label>
                <div className="block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-lg font-semibold text-white">
                  {formatIndianCurrency((calculateDiscountedBaseFee() / (parseInt(formData.installment_count) || 1)))}
                </div>
                <p className="text-sm text-gray-400 mt-1">
                  Based on discounted base fee (excluding enrollment fee)
                </p>
              </div>

              {/* Installment Dates */}
              <div>
                              <label className="block text-sm font-medium text-gray-300 mb-2">
                Installment Due Dates
              </label>
                <div className="space-y-2">
                  {installmentDates.map((date, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="date"
                        value={date}
                        onChange={(e) => handleInstallmentDateChange(index, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 bg-gray-700 text-white"
                      />
                      <button
                        type="button"
                        onClick={() => removeInstallmentDate(index)}
                        className="px-3 py-2 text-red-400 hover:text-red-300"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addInstallmentDate}
                    className="text-sm text-yellow-400 hover:text-yellow-300"
                  >
                    + Add Due Date
                  </button>
                </div>
              </div>
            </div>
          )}

          {createMutation.error && (
            <div className="bg-red-900/20 border border-red-600 rounded-md p-4">
              <p className="text-sm text-red-300">
                {createMutation.error?.response?.data?.detail || 'An error occurred. Please try again.'}
              </p>
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/fees')}
                              className="px-4 py-2 border border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Fee Structure'}
            </button>
          </div>
        </form>
      </div>

      {/* Validation Error Modal */}
      <ConfirmationModal
        isOpen={showValidationErrors}
        onClose={() => setShowValidationErrors(false)}
        title="Validation Error"
        message={validationError}
        type="warning"
        confirmText="OK"
        showCloseButton={false}
        confirmButtonVariant="primary"
      />
    </div>
  );
};

export default FeeForm; 